function getStorageKey(user) {
    if (!user || !user.userId) {
        return null;
    }

    const company = user.company || 'default';
    return `argus:requests:opened:${company}:${user.userId}`;
}

function readOpenedMap(user) {
    const key = getStorageKey(user);
    if (!key) {
        return {};
    }

    try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : {};
    } catch (e) {
        return {};
    }
}

function writeOpenedMap(user, openedMap) {
    const key = getStorageKey(user);
    if (!key) {
        return;
    }

    try {
        localStorage.setItem(key, JSON.stringify(openedMap || {}));
    } catch (e) {
        // localStorage can be unavailable in restricted WebView modes.
    }
}

function normalizeOpenedEntry(entry) {
    if (!entry) {
        return null;
    }

    if (typeof entry === 'number') {
        return Number.isFinite(entry) ? { openedAt: entry } : null;
    }

    if (typeof entry === 'string') {
        const openedAt = Number(entry);
        return Number.isFinite(openedAt) ? { openedAt } : null;
    }

    if (typeof entry === 'object') {
        const rawOpenedAt = entry.openedAt !== undefined ? entry.openedAt : entry.opened_at;
        const openedAt = Number(rawOpenedAt);
        return Number.isFinite(openedAt) ? { ...entry, openedAt } : null;
    }

    return null;
}

function getOpenedEntry(requestId, user) {
    if (!requestId) {
        return null;
    }

    const openedMap = readOpenedMap(user);
    return normalizeOpenedEntry(openedMap[String(requestId)]);
}

function parseDateTime(value) {
    if (!value) {
        return null;
    }

    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(time) ? time : null;
}

function getCommentsCount(request) {
    if (!request) {
        return 0;
    }

    const count = Number(request.comments_count);
    if (Number.isFinite(count)) {
        return Math.max(0, count);
    }

    return Array.isArray(request.comments) ? request.comments.length : 0;
}

function getCommentCreatedTimes(request) {
    if (!request) {
        return [];
    }

    const values = Array.isArray(request.comment_created_ats)
        ? request.comment_created_ats
        : (Array.isArray(request.comments) ? request.comments.map(comment => comment.created_at || comment.createdAt) : []);

    return values
        .map(parseDateTime)
        .filter(time => Number.isFinite(time));
}

export function isRequestOpened(requestId, user) {
    return Boolean(getRequestOpenedAt(requestId, user));
}

export function getRequestOpenedAt(requestId, user) {
    const entry = getOpenedEntry(requestId, user);
    return entry && entry.openedAt ? entry.openedAt : null;
}

export function getRequestNewCommentsCount(request, user) {
    const total = getCommentsCount(request);
    if (!total || !request || !request.id || !user || !user.userId) {
        return 0;
    }

    const openedAt = getRequestOpenedAt(request.id, user);
    if (!openedAt) {
        return total;
    }

    const createdTimes = getCommentCreatedTimes(request);
    if (createdTimes.length) {
        return Math.min(total, createdTimes.filter(time => time > openedAt).length);
    }

    const lastCommentAt = parseDateTime(request.last_comment_at);
    return lastCommentAt && lastCommentAt > openedAt ? 1 : 0;
}

export function markRequestOpened(requestId, user) {
    if (!requestId || !user || !user.userId) {
        return;
    }

    const openedMap = readOpenedMap(user);
    openedMap[String(requestId)] = Date.now();
    writeOpenedMap(user, openedMap);
}

export default {
    isRequestOpened,
    getRequestOpenedAt,
    getRequestNewCommentsCount,
    markRequestOpened
};
